import clsx from 'clsx'
import { useRouter } from 'expo-router'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { env } from '@/src/config/env'
import { CopyButton } from '@/src/elements/button/copy/CopyButton'
import { Typography } from '@/src/elements/text/Typography'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import type { TMediumCommunityApi } from '@/src/features/community/api/communityApi.types'
import { EVENT_ORIGIN } from '@/src/features/community/models/community.constants'
import { useUserActiveAndVisibleCommunities } from '@/src/features/community/services/useUserActiveAndVisibleCommunities'
import { CommunityCardSlider } from '@/src/features/community/ui/components/CommunityCardSlider'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { Button } from '../../../button/default'
import { Modal } from '../../../modal/Modal'
import { UserProfilePreview } from '../../UserProfilePreview/UserProfilePreview'
import { userProfilePreviewWithModalVariants } from '../user-profile-preview-with-modal-variants'

type TModalWithUserCommunitiesInfosProps = Pick<TMediumCommunityApi, 'user'> & {
  showUserProfileModal: boolean
  dismissUserProfileModal: () => void
}

export const ProfileUserModal = ({
  showUserProfileModal,
  dismissUserProfileModal,
  ...props
}: TModalWithUserCommunitiesInfosProps): ReactNode => {
  const { user } = props
  const { data: me } = useMeQuery()
  const { navigate } = useRouter()
  const { t } = useTranslate()
  const { lessThan } = useDimensions()

  const isMobileSize = lessThan.md
  const { data: userCommunities = [] } = useUserActiveAndVisibleCommunities({
    userId: user?.id,
    fields: ['hasJoined'],
  })

  const hasCreatedCommunities = userCommunities.length > 0
  const { routes } = useRoutes()

  const { modalBase, userPreviewContainer } = userProfilePreviewWithModalVariants({
    hasCreatedCommunities,
    isMobileSize,
  })

  return (
    <Modal
      blurBackdrop
      showModal={showUserProfileModal}
      fullWidth
      scrollable
      title={t('words.profile')}
      onCancel={dismissUserProfileModal}
      classNames={{ base: clsx(modalBase(), 'min-w-full bg-white'), body: 'p-0 overflow-y-visible' }}
    >
      <View className={userPreviewContainer()}>
        <UserProfilePreview.Avatar user={user} />
        <View className="gap-2 self-center">
          <Button
            colors="primary"
            variant="faded"
            startIcon={ICON.MESSAGE_CHAT}
            fullWidth={isMobileSize}
            isDisabled={!user}
            onPress={() => {
              if (user) {
                dismissUserProfileModal()
                navigate(routes.newConversationById(user.id))
              }
            }}
          >
            <FormattedMessage id="contact.send-message" />
          </Button>
          {user && me?.admin && (
            <View className="m-auto w-full flex-row items-center gap-2 rounded-full bg-default-bg-medium p-2 md:m-0 md:w-auto md:justify-end">
              {/* oxlint-disable-next-line @shopify/jsx-no-hardcoded-content */}
              <Typography className="px-2" size="sm">
                ID: {user.id}
              </Typography>
              <CopyButton link={user.id.toString()} colors="default" size="xs" startIcon={ICON.COPY} variant="light">
                <></>
              </CopyButton>
              <Button
                colors="default"
                variant="light"
                size="xs"
                startIcon={ICON.USER_EDIT}
                onPress={() =>
                  navigate(
                    `${env.EXPO_PUBLIC_APP_BASE_URL?.replace('//', '//admin.').replace('/api', '')}/${user.lang}/user/${user.id}`,
                  )
                }
              />
            </View>
          )}
        </View>
      </View>
      {hasCreatedCommunities && (
        <View className="px-6">
          <CommunityCardSlider
            onPress={dismissUserProfileModal}
            testID={TEST_ID.PROFILE.CREATED_COMMUNITIES}
            maxNbItems={userCommunities.length}
            classes={{
              titleContainer: '!pt-4 xm:!pt-8 !px-4 xm:!px-0',
              title: '!text-default-text-contrast-low !text-lg',
            }}
            color="default"
            title={t('user.communities')}
            list={userCommunities}
            source={TRACKING_SOURCE.PROFILE_PAGE}
            origin={EVENT_ORIGIN.PROFILE_ANIMATOR}
            showSubscribeButton
          />
        </View>
      )}
    </Modal>
  )
}
